import { formatDisplayDate } from '../../lib/format';
import { dashboardT, type DashboardMessageKey } from '../../lib/messages';
import { ChangelogCategory, type ChangelogEntry } from './model';

interface CategoryPresentation {
  labelKey: DashboardMessageKey;
  contextKeys: readonly string[];
}

const categoryPresentation: Record<ChangelogCategory, CategoryPresentation> = {
  [ChangelogCategory.Placement]: {
    labelKey: 'changelog.categories.placement',
    contextKeys: ['placementName', 'placementId'],
  },
  [ChangelogCategory.Publisher]: {
    labelKey: 'changelog.categories.publisher',
    contextKeys: ['publisherName', 'publisherId'],
  },
  [ChangelogCategory.Integration]: {
    labelKey: 'changelog.categories.integration',
    contextKeys: ['integrationName', 'integrationId'],
  },
  [ChangelogCategory.LineItem]: {
    labelKey: 'changelog.categories.lineItem',
    contextKeys: ['lineItemName', 'lineItemId'],
  },
  [ChangelogCategory.PublisherDomain]: {
    labelKey: 'changelog.categories.publisherDomain',
    contextKeys: ['domain', 'domainUrl', 'publisherDomainId'],
  },
  [ChangelogCategory.PublisherApp]: {
    labelKey: 'changelog.categories.publisherApp',
    contextKeys: ['appName', 'appId'],
  },
  [ChangelogCategory.App]: {
    labelKey: 'changelog.categories.app',
    contextKeys: ['appName', 'appId'],
  },
  [ChangelogCategory.Targeting]: {
    labelKey: 'changelog.categories.targeting',
    contextKeys: ['targetingName', 'targetingId'],
  },
  [ChangelogCategory.AdsTxt]: {
    labelKey: 'changelog.categories.adsTxt',
    contextKeys: ['adstxtRecordId'],
  },
  [ChangelogCategory.DemandPartner]: {
    labelKey: 'changelog.categories.demandPartner',
    contextKeys: ['demandPartnerDisplayName', 'demandPartnerId'],
  },
  [ChangelogCategory.Deal]: {
    labelKey: 'changelog.categories.deal',
    contextKeys: ['dealName', 'dealId'],
  },
  [ChangelogCategory.Advertiser]: {
    labelKey: 'changelog.categories.advertiser',
    contextKeys: ['advertiserName', 'advertiserId'],
  },
  [ChangelogCategory.Order]: {
    labelKey: 'changelog.categories.order',
    contextKeys: ['orderName', 'orderId'],
  },
  [ChangelogCategory.Creative]: {
    labelKey: 'changelog.categories.creative',
    contextKeys: ['creativeName', 'creativeId'],
  },
  [ChangelogCategory.Video]: {
    labelKey: 'changelog.categories.video',
    contextKeys: ['videoTitle', 'videoId'],
  },
  [ChangelogCategory.ContentOwner]: {
    labelKey: 'changelog.categories.contentOwner',
    contextKeys: ['contentOwnerName', 'contentOwnerId'],
  },
  [ChangelogCategory.Widget]: {
    labelKey: 'changelog.categories.widget',
    contextKeys: ['widgetName', 'widgetId'],
  },
  [ChangelogCategory.User]: {
    labelKey: 'changelog.categories.user',
    contextKeys: ['username', 'userId'],
  },
  [ChangelogCategory.Asset]: {
    labelKey: 'changelog.categories.asset',
    contextKeys: ['assetName', 'assetId'],
  },
  [ChangelogCategory.Brand]: {
    labelKey: 'changelog.categories.brand',
    contextKeys: ['brandName', 'brandId'],
  },
  [ChangelogCategory.A2vBrand]: {
    labelKey: 'changelog.categories.a2vBrand',
    contextKeys: ['brandName', 'a2vBrandName', 'brandId'],
  },
  [ChangelogCategory.Playlist]: {
    labelKey: 'changelog.categories.playlist',
    contextKeys: ['playlistTitle', 'playlistId'],
  },
  [ChangelogCategory.A2vConfiguration]: {
    labelKey: 'changelog.categories.a2vConfiguration',
    contextKeys: ['configurationName', 'networkName'],
  },
};

const fieldLabelKeys: Partial<Record<ChangelogCategory, Record<string, DashboardMessageKey>>> = {
  [ChangelogCategory.Placement]: {
    name: 'changelog.fields.name',
    secondaryId: 'changelog.fields.alternativeId',
    status: 'changelog.fields.status',
    deliveryType: 'changelog.fields.delivery',
    bidFloor: 'changelog.fields.bidFloor',
    qpsLimit: 'changelog.fields.qpsLimit',
    maxAdDuration: 'changelog.fields.maxAdDuration',
    shareOfVoice: 'changelog.fields.shareOfVoice',
    description: 'changelog.fields.description',
    notes: 'changelog.fields.notes',
    type: 'changelog.fields.type',
  },
  [ChangelogCategory.Publisher]: {
    name: 'changelog.fields.name',
    companyName: 'changelog.fields.companyName',
    contactName: 'changelog.fields.contactName',
    email: 'changelog.fields.email',
    status: 'changelog.fields.status',
    phone: 'changelog.fields.phone',
    websiteUrl: 'changelog.fields.website',
    address: 'changelog.fields.address',
    city: 'changelog.fields.city',
    state: 'changelog.fields.state',
    zipCode: 'changelog.fields.zipCode',
    country: 'changelog.fields.country',
  },
  [ChangelogCategory.Integration]: {
    sellerDomain: 'changelog.fields.sellerDomain',
    sellerId: 'changelog.fields.sellerId',
    cpm: 'changelog.fields.cpm',
    bidFloor: 'changelog.fields.bidFloor',
    qps: 'changelog.fields.qpsLimit',
    shareOfVoice: 'changelog.fields.shareOfVoice',
    status: 'changelog.fields.status',
  },
  [ChangelogCategory.DemandPartner]: {
    displayName: 'changelog.fields.name',
    status: 'changelog.fields.status',
    bidderCode: 'changelog.fields.bidderCode',
    address: 'changelog.fields.address',
    qps: 'changelog.fields.qpsLimit',
  },
  [ChangelogCategory.LineItem]: {
    name: 'changelog.fields.name',
    status: 'changelog.fields.status',
    grossBudget: 'changelog.fields.grossBudget',
    agencyFee: 'changelog.fields.agencyFee',
    cpm: 'changelog.fields.cpm',
    impressionsGoal: 'changelog.fields.impressionsGoal',
    startDate: 'changelog.fields.startDate',
    endDate: 'changelog.fields.endDate',
    dayTimeActivities: 'changelog.fields.scheduling',
    pacing: 'changelog.fields.pacing',
  },
  [ChangelogCategory.User]: {
    email: 'changelog.fields.email',
    name: 'changelog.fields.name',
    enabled: 'changelog.fields.status',
    roles: 'changelog.fields.roles',
    publishers: 'changelog.fields.publishers',
  },
  [ChangelogCategory.Asset]: {
    name: 'changelog.fields.name',
    type: 'changelog.fields.type',
    tags: 'changelog.fields.tags',
  },
};

export function changelogCategoryLabel(category: ChangelogCategory) {
  return dashboardT(categoryPresentation[category].labelKey);
}

export function changelogContextLabel(entry: ChangelogEntry) {
  const value = categoryPresentation[entry.category].contextKeys
    .map((key) => entry.context[key])
    .find((candidate) => candidate !== null && String(candidate ?? '').trim());
  return value === undefined ? changelogCategoryLabel(entry.category) : String(value);
}

export function changelogFieldLabel(entry: Pick<ChangelogEntry, 'category' | 'fieldName'>) {
  const key = fieldLabelKeys[entry.category]?.[entry.fieldName];
  return key ? dashboardT(key) : humanizeFieldName(entry.fieldName);
}

export function formatChangelogDate(value: string) {
  return formatDisplayDate(value, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function displayChangelogValue(value: string | undefined) {
  return value === undefined || value === '' ? dashboardT('changelog.notAvailable') : value;
}

function humanizeFieldName(value: string) {
  const leaf = value.split('.').at(-1) ?? value;
  return leaf
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replaceAll('_', ' ')
    .replace(/^./, (character) => character.toUpperCase());
}
